// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import React, { useEffect, useState } from 'react';
const _preserve = React.version;

import BreadcrumbGroup from '@cloudscape-design/components/breadcrumb-group';
import Container from '@cloudscape-design/components/container';
import Form from '@cloudscape-design/components/form';
import Header from '@cloudscape-design/components/header';


import { FileUpload } from '@cloudscape-design/components';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { addYears, format, getMonth, isBefore, parse, startOfDay } from 'date-fns';
import { fetchWithWatch } from '../../../../hooks/fetchWithWatch';
import { ItemUser } from '../../../../resources/types';
import { addNotificationStatus, buildBreadcrumbItems, getCookie, getUrlVersion, setData, setLoading } from '../../../commons/functions';
import { useGlobalState } from '../../../context/GlobalStateContext';
import { CustomAppLayout } from '../../layout';

export const UserStudentPage = () => {
    const { idUser, menu, RUT } = useGlobalState();
    const [files, setFiles] = React.useState<File[]>([])

    const [user, setUser] = useState<ItemUser>();
    const handleRefresh = async () => {
        try {

            setLoading(true);
            const response = await fetchWithWatch(`${getUrlVersion()}/routing/v1/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-routing': '/private/user/home',
                    'JWT': getCookie("JWT"),

                },
                body: JSON.stringify({
                    movements: true,
                    hash: "home"
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error: ${response.status}`);
            }

            const dataWeb = await response.json();

            if (dataWeb.code === 0) {
                if (dataWeb.user && dataWeb?.user?.studentCertification) {
                    setUser(dataWeb.user);
                } else {
                    window.location.hash = "#home";
                }
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);

        }
    };
    useEffect(() => {
        handleRefresh();
    }, []);

    const handleFileChange = async (files: File[]) => {
        try {
            // Use Promise.all to concurrently convert all files to Base64
            const base64Results = await Promise.all(files.map(fileToBase64));

            try {
                setLoading(true);
                const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
                const filename = `${user.RUT}_certificado_estudiante_${Date.now()}.pdf`;
                const response = await fetchWithWatch(`${getUrlVersion()}/routing/v1/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-routing': '/private/admin/documents/files/studentcertification',
                        'JWT': getCookie("JWT"),
                    },

                    body: JSON.stringify({
                        idUser: idUser,
                        fileName: `${filename}`,
                        fileType: files[0].name.split('.').pop()?.toLocaleLowerCase() || "",
                        fileSize: files[0].size,
                        file: base64Results[0].base64, // assuming single file upload
                    })
                });
                if (!response.ok) { throw new Error(`HTTP error: ${response.status}`); }

                const data = await response.json();
                addNotificationStatus(data, setData);
                if (data.code === 0) {
                    handleRefresh();
                }

            } catch (error) {
                console.error('Error fetching users:', error);
            } finally {
                setLoading(false);
            }
        } catch (error) {
            console.error('Error reading files:', error);
            // Handle error state if necessary
        }
    };
    const isPastDate = user && user.studentCertification ? isBefore(parse(user.studentCertification, "dd-MM-yyyy", new Date()), startOfDay(new Date())) : false;

    const semester = getMonth(new Date()) < 4 ?
        `primer semestre ${format(addYears(new Date(), 0), 'yyyy')}` : getMonth(new Date()) < 9 ?

            `segundo semestre ${format(new Date(), 'yyyy')}` : `primer semestre ${format(new Date(), 'yyyy')}`;

    return (
        <>
            <CustomAppLayout
                breadcrumbs={
                    <BreadcrumbGroup
                        items={buildBreadcrumbItems(menu)}
                        expandAriaLabel="Show path"
                        ariaLabel="Breadcrumbs"
                    />
                }
                toolsHide={true}
                contentType="form"
                content={
                    <Form
                        header={
                            <Header variant="h1"
                            >Beneficio estudiantes
                            </Header>
                        }>
                        <SpaceBetween size="xl">
                            <Container header={''}>
                                <SpaceBetween size="m" direction="vertical">
                                    {isPastDate ? (<>
                                        <p>
                                            Estimado socio,</p><p>De acuerdo a nuestros registros usted tiene cargos asociados a la condición de <b>estudiante</b>. La justificación del vencimiento caducó el <b>{user?.studentCertification}</b>. Para continuar con este beneficio, usted debe presentar su certificado de alumno regular emitido por su establecimiento educacional para el periodo <em>{semester}</em>.</p><p>En caso de no poseer la condición de estudiante, comuniquese con la oficina de administración para que sus cargos sean actualizados y pueda continuar utilizando el Sistema CPV</p>
                                    </>) : (
                                        <>
                                            <p>Estimado socio,</p><p>De acuerdo a nuestros registros usted tiene cargos asociados a la condición de <b>estudiante</b>. El vencimiento de justificación de este beneficio es el <b>{user?.studentCertification}</b>. Para continuar con este beneficio, usted debe presentar su certificado de alumno regular emitido por su establecimiento educacional para el periodo <em>{semester}</em>.</p><p>A continuación usted puede realizar el envio de su certificado, el cual será validado en un plazo de 48 horas hábiles</p>

                                        </>
                                    )}
                                    <FileUpload
                                        onChange={({ detail }) => {
                                            handleFileChange(detail.value);
                                        }}
                                        accept='.pdf'
                                        i18nStrings={{
                                            uploadButtonText: e => "Subir certificado",
                                            dropzoneText: e => "Arrastre archivo para cargar (Solo PDF)",
                                            removeFileAriaLabel: e => `Eliminar archivo ${e + 1}`,
                                            limitShowFewer: "Show fewer files",
                                            limitShowMore: "Show more files",
                                            errorIconAriaLabel: "Error",
                                            warningIconAriaLabel: "Warning"
                                        }}
                                        showFileLastModified
                                        showFileSize
                                        multiple={false}
                                        tokenLimit={1} value={[]} />
                                </SpaceBetween>


                            </Container>
                        </SpaceBetween>

                    </Form>
                }
            />
        </>
    );
};


export const fileToBase64 = (file: File): Promise<{ base64: string; file: File }> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        // When the file reading is complete
        reader.onload = () => {
            // The result will be a data URL (e.g., "data:image/png;base64,iVB...")
            // We strip the header (e.g., "data:image/png;base64,") to get pure Base64
            const base64String = reader.result ? (reader.result as string).split(',')[1] : '';
            resolve({ base64: base64String, file });
        };

        // Handle errors during reading
        reader.onerror = (error) => reject(error);

        // Read the file content as a Data URL (which contains Base64)
        reader.readAsDataURL(file);
    });
};